import React, { useEffect, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CircularProgress from "@material-ui/core/CircularProgress";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import DocumentTemplateEditor from "../../components/DocumentTemplateEditor";
import { DEFAULT_PAGE_CONFIG } from "../../components/DocumentTemplateEditor/constants";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  headerFields: {
    marginBottom: theme.spacing(1),
  },
  editorWrapper: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
  },
}));

const DocumentTemplateEditorPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { templateId } = useParams();

  const [loading, setLoading] = useState(Boolean(templateId));
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);
  const [pageConfig, setPageConfig] = useState(DEFAULT_PAGE_CONFIG);
  const [initialContent, setInitialContent] = useState(null);
  const editorRef = useRef(null);

  useEffect(() => {
    if (!templateId) {
      setInitialContent("<p></p>");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/document-templates/${templateId}`);
        setName(data.name || "");
        setDescription(data.description || "");
        setActive(data.active !== false);
        setPageConfig({ ...DEFAULT_PAGE_CONFIG, ...(data.pageConfig || {}) });
        setInitialContent(data.contentHtml || "<p></p>");
      } catch (err) {
        toastError(err);
        history.push("/document-templates");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  const handleSave = async () => {
    if (!editorRef.current) return;
    if (!name.trim()) {
      toast.error(i18n.t("documentTemplates.editor.name"));
      return;
    }

    const payload = {
      name: name.trim(),
      description,
      contentHtml: editorRef.current.getHTML(),
      pageConfig,
      active,
    };

    setSaving(true);
    try {
      if (templateId) {
        await api.put(`/document-templates/${templateId}`, payload);
      } else {
        const { data } = await api.post("/document-templates", payload);
        history.replace(`/document-templates/editor/${data.id}`);
      }
      toast.success(i18n.t("documentTemplates.toasts.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainContainer className={classes.container}>
      <MainHeader>
        <Grid
          container
          spacing={1}
          alignItems="center"
          className={classes.headerFields}
        >
          <Grid item>
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={() => history.push("/document-templates")}
            >
              {i18n.t("documentTemplates.title")}
            </Button>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={i18n.t("documentTemplates.editor.name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={i18n.t("documentTemplates.editor.description")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={
                <Switch
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  color="primary"
                />
              }
              label={i18n.t("documentTemplates.editor.active")}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              disabled={saving || loading}
              onClick={handleSave}
            >
              {saving ? (
                <CircularProgress size={20} />
              ) : (
                i18n.t("documentTemplates.buttons.save")
              )}
            </Button>
          </Grid>
        </Grid>
      </MainHeader>
      <div className={classes.editorWrapper}>
        {loading || initialContent === null ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : (
          <DocumentTemplateEditor
            initialContent={initialContent}
            pageConfig={pageConfig}
            onPageConfigChange={setPageConfig}
            onEditorReady={(editor) => {
              editorRef.current = editor;
            }}
          />
        )}
      </div>
    </MainContainer>
  );
};

export default DocumentTemplateEditorPage;
